import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  useTheme,
  alpha,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import { format } from 'date-fns';
import axiosInstance from '../utils/axios';
import { useSnackbar } from 'notistack';

const UserBalanceHistoryDialog = ({ open, onClose, user }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (open && user) {
      fetchHistory();
    }
  }, [open, user, startDate, endDate]);

  const fetchHistory = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const response = await axiosInstance.get(`/telegram-users/users/${user.userId}/balance-history`, { params });
      const balanceHistory = Array.isArray(response.data) ? response.data : [];
      
      const transactions = balanceHistory.map(entry => ({
        id: entry._id,
        type: entry.type === 'admin_add' ? 'deposit' : 
              entry.type === 'admin_subtract' ? 'admin_subtract' :
              entry.type === 'withdraw' ? 'purchase' : entry.type,
        amount: entry.amount,
        date: new Date(entry.createdAt),
        description: entry.description,
        balanceBefore: entry.balanceBefore,
        balanceAfter: entry.balanceAfter,
        adminUsername: entry.adminUsername
      }));

      setHistory(transactions);
    } catch (error) {
      console.error('Error fetching history:', error);
      enqueueSnackbar('Ошибка загрузки истории', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(Math.abs(value) || 0);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <IncomeIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
      case 'purchase':
      case 'admin_subtract':
        return <ExpenseIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
      default:
        return <WalletIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'deposit':
        return 'Пополнение';
      case 'admin_subtract':
        return 'Списание';
      case 'purchase':
        return 'Покупка';
      default:
        return 'Операция';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'deposit':
        return 'success';
      case 'admin_subtract':
        return 'warning';
      case 'purchase':
        return 'error';
      default:
        return 'default';
    }
  };

  const totalDeposits = history.filter(t => t.type === 'deposit').reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const totalSpent = history.filter(t => (t.type === 'purchase' || t.type === 'admin_subtract')).reduce((sum, t) => sum + Math.abs(t.amount), 0);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box display="flex" alignItems="center">
            <WalletIcon sx={{ mr: 1 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                История баланса
              </Typography>
              <Typography variant="caption">
                {user?.username ? `@${user.username}` : user?.firstName} (ID: {user?.userId})
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <WalletIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary">
                      Текущий баланс
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {formatCurrency(user?.balance || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <IncomeIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                    <Typography variant="body2" color="text.secondary">
                      Всего пополнено
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {formatCurrency(totalDeposits)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <ExpenseIcon sx={{ mr: 1, color: theme.palette.error.main }} />
                    <Typography variant="body2" color="text.secondary">
                      Всего потрачено
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {formatCurrency(totalSpent)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <DatePicker
                  label="Дата начала"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <DatePicker
                  label="Дата окончания"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchHistory}
                  disabled={loading}
                  size="large"
                >
                  Применить
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* History Table */}
          {loading ? (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress />
            </Box>
          ) : history.length === 0 ? (
            <Box p={6} textAlign="center">
              <WalletIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Нет операций
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Дата</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Тип</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Описание</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Сумма</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Баланс после</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {format(transaction.date, 'dd.MM.yyyy HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getTypeIcon(transaction.type)}
                          <Chip
                            label={getTypeLabel(transaction.type)}
                            size="small"
                            color={getTypeColor(transaction.type)}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transaction.description}
                        </Typography>
                        {transaction.adminUsername && (
                          <Typography variant="caption" color="text.secondary">
                            Админ: {transaction.adminUsername}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                        >
                          {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600">
                          {formatCurrency(transaction.balanceAfter)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={onClose} variant="outlined">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default UserBalanceHistoryDialog;
